import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from "../components/ui/button";
import landing from '../assets/land3.jpg'

const Hero = ({ onNavigate }) => {
  const { t } = useTranslation();

  const getWindowWidth = () => (typeof window !== 'undefined' ? window.innerWidth : 1200);
  const getWindowHeight = () => (typeof window !== 'undefined' ? window.innerHeight : 800);
  const centerX = getWindowWidth() / 2;
  const spreadX = getWindowWidth() * .4; // مدى الانتشار حول المركز (80% من العرض مثلاً)

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src={landing}
          alt="Modern Furniture"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50"></div>
      </div>

      {/* Floating Elements - Modified for mobile */}
      <div dir='ltr' className="absolute inset-0 overflow-hidden">
        {[...Array(100)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-amber-400/40"
            initial={{
              x: centerX + (Math.random() - 0.5) * spreadX, // حول المركز
              y: Math.random() * getWindowHeight(),
              opacity: [.5, .85, 1],
            }}
            animate={{
              y: [null, Math.random() * getWindowHeight()],
              x: [null, centerX + (Math.random() - 0.5) * spreadX], // حول المركز
              opacity: [.5, .85, 1],
            }}
            transition={{
              duration: Math.random() * 40 + 50,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-full px-4 text-center text-white mobile-container overflow-hidden">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="max-w-4xl mx-auto w-full"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 bg-amber-500/20 backdrop-blur-sm border border-amber-400/30 rounded-full px-3 py-1.5 mb-4 sm:px-4 sm:py-2 sm:mb-6 max-w-full"
          >
            <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-amber-400 flex-shrink-0" />
            <span className="text-sm sm:text-lg font-medium whitespace-nowrap overflow-hidden text-ellipsis">{t('hero.premiumCollection')}</span>
          </motion.div>

          {/* Main Title */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            style={{ lineHeight: '1.5' }}
            className="text-3xl sm:text-5xl md:text-7xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-white to-amber-200 bg-clip-text text-transparent max-w-full break-words"
          >
            {t('hero.title')}
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-lg sm:text-xl md:text-2xl mb-3 sm:mb-4 text-gray-200 font-light max-w-full break-words"
          >
            {t('hero.subtitle')}
          </motion.p>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="text-base sm:text-lg mb-6 sm:mb-8 text-gray-300 max-w-2xl mx-auto leading-relaxed max-w-full break-words px-2"
          >
            {t('hero.description')}
          </motion.p>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
            className="w-full flex justify-center"
          >
            <Button
              onClick={() => onNavigate('catalog')}
              size="lg"
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold px-6 py-4 sm:px-8 sm:py-6 rounded-full text-base sm:text-lg transition-all duration-300 transform hover:scale-95 shadow-lg hover:shadow-xl group max-w-full"
            >
              <span className="truncate">{t('hero.cta')}</span>
              <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform flex-shrink-0" />
            </Button>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-5 h-8 sm:w-10 sm:h-16 border-2 border-white/30 rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 h-2 sm:h-3 bg-white rounded-full mt-1 sm:mt-2"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;